import { type FC } from "react"
import TextAreaAutoSize from "react-textarea-autosize"

import { landingPageCopyCharacterLimit } from "@forento/core/schemas/landing"
import { cn, tw } from "@forento/ui/utilities/tailwind"

import CharacterCounter from "./CharacterCounter"

const sharedInputClassName = tw`w-full rounded-md border-none bg-transparent p-0 text-inherit placeholder:text-gray-400 focus:outline-none`

type Props = {
	value: string
	onChange: (value: string) => void
	placeholder: string
	className?: string
	inputClassName?: string
	multiline?: boolean
	minRows?: number
	fitContent?: boolean
	fitContentMaxSize?: number
	maxCharacters?: number
	counterClassName?: string
	showCharacterCounter?: boolean
}
const EditableField: FC<Props> = ({
	value,
	onChange,
	placeholder,
	className,
	inputClassName,
	multiline = false,
	minRows = 1,
	fitContent = false,
	fitContentMaxSize = 32,
	maxCharacters,
	counterClassName,
	showCharacterCounter = true,
}) => {
	const handleChange = (nextValue: string) => {
		onChange(maxCharacters === undefined ? nextValue : nextValue.slice(0, maxCharacters))
	}
	const fitContentSize = Math.min(Math.max(value.length, 1), fitContentMaxSize)

	return (
		<div
			className={cn(
				"min-w-0 rounded-md transition-shadow duration-150 focus-within:ring-2 focus-within:ring-stone-300/70",
				fitContent && "max-w-full",
				className,
			)}
		>
			{multiline ? (
				<TextAreaAutoSize
					value={value}
					onChange={event => handleChange(event.target.value)}
					placeholder={placeholder}
					minRows={minRows}
					maxLength={maxCharacters}
					className={cn(sharedInputClassName, "resize-none", inputClassName)}
				/>
			) : (
				<input
					type="text"
					value={value}
					onChange={event => handleChange(event.target.value)}
					placeholder={placeholder}
					maxLength={maxCharacters}
					size={fitContent ? fitContentSize : undefined}
					className={cn(sharedInputClassName, fitContent ? "w-auto max-w-full" : "w-full", inputClassName)}
				/>
			)}
			{showCharacterCounter && maxCharacters !== undefined && (
				<CharacterCounter
					count={value.length}
					limit={maxCharacters ?? landingPageCopyCharacterLimit}
					className={counterClassName}
				/>
			)}
		</div>
	)
}

export default EditableField
